import { useCallback, useEffect, useReducer, useRef, useState } from "react";

export const OCR_SESSION_KEY = "document-processing-active-ocr-v1";
const terminalStatuses = new Set(["success", "error", "cancelled"]);
const localModes = new Set(["local_ocr", "local_ocr_latex"]);
const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
const readPending = () => {
  try {
    const records = JSON.parse(sessionStorage.getItem(OCR_SESSION_KEY) || "[]");
    return Array.isArray(records) ? records : [];
  }
  catch { return []; }
};
const writePending = (records) => {
  try { sessionStorage.setItem(OCR_SESSION_KEY, JSON.stringify(records)); } catch { /* Private storage may be unavailable. */ }
};
const remember = (record) => writePending([
  ...readPending().filter((item) => item.clientRequestId !== record.clientRequestId), record,
]);
const forget = (record) => writePending(readPending().filter((item) => item.clientRequestId !== record.clientRequestId));
const newRequestId = () => window.crypto?.randomUUID?.()
  || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (letter) => {
    const value = Math.floor(Math.random() * 16);
    return (letter === "x" ? value : (value & 3) | 8).toString(16);
  });

/** Owns only this tab's OCR requests; the queue can also show other owned jobs. */
export default function useLocalOcrJobs({ enabled, authToken, startJob, getJob, cancelJob, listJobs }) {
  const api = useRef({ startJob, getJob, cancelJob, listJobs });
  api.current = { startJob, getJob, cancelJob, listJobs };
  const mounted = useRef(false);
  const recovered = useRef(false);
  const current = useRef(null);
  const listSequence = useRef(0);
  const cancelling = useRef(new Map());
  const [, redraw] = useReducer((value) => value + 1, 0);
  const [jobs, setJobs] = useState([]);
  const [listError, setListError] = useState("");
  const [notice, setNotice] = useState("");
  const [cancelErrors, setCancelErrors] = useState({});
  const notify = useCallback(() => { if (mounted.current) redraw(); }, []);
  const invalidateList = useCallback(() => { ++listSequence.current; }, []);

  const refresh = useCallback(async () => {
    if (!api.current.listJobs) return;
    const sequence = ++listSequence.current;
    let response;
    try { response = await api.current.listJobs(); } catch { response = { success: false }; }
    if (!mounted.current || sequence !== listSequence.current) return;
    if (response?.success) {
      setJobs((response.jobs || []).filter((job) => localModes.has(job.mode) && !terminalStatuses.has(job.status)));
      setListError("");
    } else setListError("尚無法讀取 OCR 工作清單，請重試。");
  }, []);

  const requestCancellation = useCallback((record, { keepalive = false, run = null } = {}) => {
    const key = record.jobId || record.clientRequestId;
    if (cancelling.current.has(key)) return cancelling.current.get(key);
    if (record.clientRequestId) remember({ ...record, needsCancel: true });
    if (run) run.stopRequested = true;
    if (mounted.current) setCancelErrors((errors) => ({ ...errors, [key]: "" }));
    const pending = (async () => {
      let result;
      try {
        result = await api.current.cancelJob?.(record.jobId, {
          clientRequestId: record.clientRequestId, keepalive,
        });
      } catch { result = null; }
      const terminal = result?.job || (result?.cancelled ? { status: "cancelled" } : null);
      if (result?.success && terminalStatuses.has(terminal?.status)) {
        if (record.clientRequestId) forget(record);
        if (run && !run.terminal) {
          run.terminal = terminal;
          run.resolveTerminal(terminal);
        }
        if (mounted.current) {
          ++listSequence.current;
          setJobs((items) => items.filter((job) => job.id !== record.jobId));
          if (!run || (current.current === run && run.isCurrent())) {
            const outcome = terminal.status === "cancelled"
              ? "已停止解析，已完成的文字會保留。"
              : terminal.status === "success" ? "這個工作已完成，無需停止。" : "這個工作已結束。";
            setNotice(run ? outcome : `${record.filename || "選取的 OCR 工作"}：${outcome}`);
          }
          refresh();
        }
      } else if (mounted.current) {
        setCancelErrors((errors) => ({ ...errors, [key]: "無法確認停止，工作可能仍在處理，請重試。" }));
      }
      return result;
    })().finally(() => {
      cancelling.current.delete(key);
      notify();
    });
    cancelling.current.set(key, pending);
    notify();
    return pending;
  }, [notify, refresh]);

  useEffect(() => {
    mounted.current = true;
    const leave = () => {
      const run = current.current;
      readPending().forEach((record) => requestCancellation(record, {
        keepalive: true, run: run?.record.clientRequestId === record.clientRequestId ? run : null,
      }));
    };
    const returnToPage = () => {
      refresh();
      readPending().filter((record) => record.needsCancel).forEach((record) => {
        const run = current.current;
        requestCancellation(record, { run: run?.record.clientRequestId === record.clientRequestId ? run : null });
      });
    };
    window.addEventListener("pagehide", leave);
    window.addEventListener("pageshow", returnToPage);
    return () => {
      mounted.current = false;
      if (current.current) current.current.abandoned = true;
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", returnToPage);
      leave();
    };
  }, [refresh, requestCancellation]);

  useEffect(() => {
    // Recover once after authentication becomes available. Refreshing an access
    // token during a live request must not be mistaken for leaving the page.
    if (!recovered.current && authToken && api.current.cancelJob) {
      recovered.current = true;
      readPending().forEach((record) => requestCancellation(record));
    }
  }, [authToken, requestCancellation]);

  useEffect(() => {
    if (!enabled || !authToken) return undefined;
    refresh();
    const timer = setInterval(refresh, 10000);
    return () => { clearInterval(timer); invalidateList(); };
  }, [enabled, authToken, refresh, invalidateList]);

  const runJob = async (file, mode, quality, { isCurrent, onProgress }) => {
    const run = { record: { clientRequestId: newRequestId(), filename: file.name }, isCurrent, terminal: null };
    run.terminalPromise = new Promise((resolve) => { run.resolveTerminal = resolve; });
    current.current = run;
    remember(run.record);
    setNotice("");
    notify();
    let started;
    try {
      started = await Promise.race([
        api.current.startJob(file, mode, quality, { clientRequestId: run.record.clientRequestId }),
        run.terminalPromise.then((job) => ({ success: true, job })),
      ]);
    } catch { started = { success: false, error: "無法建立 OCR 工作，請檢查連線。" }; }
    if (!started?.success || !started.job?.id) {
      // A lost create response can still leave a server job. Retain its request
      // id so Stop / reload can cancel it without guessing another tab's job.
      if (run.stopRequested || run.abandoned) await requestCancellation(run.record, { run });
      return run.terminal || started;
    }
    run.record.jobId = started.job.id;
    if (!run.terminal) remember({ ...run.record, needsCancel: Boolean(run.stopRequested || run.abandoned) });
    notify();
    refresh();
    if (terminalStatuses.has(started.job.status)) {
      run.terminal = started.job;
      forget(run.record);
    } else if (run.stopRequested || run.abandoned || !mounted.current) {
      await requestCancellation(run.record, { run, keepalive: !mounted.current });
    }
    let job = run.terminal || started.job;
    while (mounted.current && isCurrent() && !run.abandoned) {
      // A confirmed stop always wins over a polling response already in flight.
      job = run.terminal || job;
      if (terminalStatuses.has(job.status)) {
        run.terminal = job;
        forget(run.record);
        notify();
        refresh();
        return job;
      }
      run.latestJob = job;
      notify();
      onProgress(job);
      await Promise.race([delay(1200), run.terminalPromise]);
      if (run.terminal) continue;
      if (!mounted.current || !isCurrent() || run.abandoned) break;
      const response = await Promise.race([
        api.current.getJob(run.record.jobId).catch(() => ({ success: false })),
        run.terminalPromise.then((terminal) => ({ success: true, job: terminal })),
      ]);
      if (run.terminal) continue;
      if (!response?.success || !response.job) {
        return { status: "error", error: response?.error || "無法讀取解析進度；可在目前 OCR 工作中確認或停止。" };
      }
      job = response.job;
    }
    return null;
  };

  const stop = () => {
    const run = current.current;
    if (run && !run.terminal) return requestCancellation(run.record, { run });
    return undefined;
  };
  const cancelQueuedJob = (job) => {
    const run = current.current;
    const matchingRun = run?.record.jobId === job.id ? run : null;
    return requestCancellation(matchingRun?.record || { jobId: job.id, filename: job.filename }, { run: matchingRun });
  };
  const active = current.current && !current.current.terminal && current.current.isCurrent()
    ? current.current : null;
  const activeKey = active?.record.jobId || active?.record.clientRequestId;
  return {
    runJob, stop, jobs, refresh, cancelQueuedJob, listError, notice, cancelErrors,
    canStop: Boolean(active), stopping: cancelling.current.has(activeKey),
    currentJobId: active?.record.jobId,
    currentJob: active ? { filename: active.record.filename, status: "creating", ...active.latestJob, id: active.record.jobId } : null,
    stopError: cancelErrors[activeKey] || cancelErrors[active?.record.clientRequestId] || "",
    recoveryError: readPending().some((record) => record.needsCancel && record.clientRequestId !== active?.record.clientRequestId
      && (cancelErrors[record.jobId] || cancelErrors[record.clientRequestId]))
      ? "上次的 OCR 工作尚未確認停止，請按重新整理工作再試。" : "",
    isCancelling: (id) => cancelling.current.has(id),
    retryRecovery: () => readPending().filter((record) => record.needsCancel).forEach((record) => requestCancellation(record)),
  };
}
